import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/i18n';
import { Home, Search, Calendar, Bell, User, MessageCircle, Map, Plus } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Logo } from '@/components/Logo';

const Badge = ({ n }) => n > 0 ? (
  <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center ring-2 ring-background">
    {n > 9 ? '9+' : n}
  </span>
) : null;

// Mobile bottom bar + desktop glass top bar (single source of truth for navigation)
const BottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, unreadCount, unreadMessagesCount } = useAuth();
  const { t } = useLanguage();

  const navItems = [
    { path: '/feed', icon: Home, label: t('nav.feed') },
    { path: '/search', icon: Search, label: t('nav.search') },
    { path: '/messages', icon: MessageCircle, label: 'Chat', badge: unreadMessagesCount },
    { path: '/notifications', icon: Bell, label: t('nav.notifications'), badge: unreadCount },
    { path: '/profile', icon: User, label: t('nav.profile') },
  ];
  const desktopExtra = [
    { path: '/calendar', icon: Calendar, label: 'Agenda' },
    { path: '/map', icon: Map, label: 'Carte' },
  ];

  const isActive = (path) => {
    if (path === '/profile') return location.pathname === '/profile' || location.pathname.startsWith('/profile/');
    return location.pathname === path;
  };
  const testId = (label) => `nav-${label.toLowerCase().replace('é', 'e')}`;

  return (
    <>
      {/* Desktop header */}
      <header className="hidden md:flex fixed top-0 inset-x-0 z-50 h-16 glass border-b items-center px-6" data-testid="desktop-nav">
        <button onClick={() => navigate('/feed')} className="mr-8" data-testid="desktop-nav-logo"><Logo /></button>
        <nav className="flex items-center gap-1">
          {[...navItems.slice(0, 2), ...desktopExtra, ...navItems.slice(2, 4)].map((item) => (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`relative flex items-center gap-2 rounded-xl px-3.5 py-2 text-sm font-medium transition-colors ${isActive(item.path) ? 'bg-foreground text-background' : 'text-muted-foreground hover:text-foreground hover:bg-muted'}`}
              data-testid={`desktop-${testId(item.label)}`}
            >
              <item.icon className="h-4 w-4" />
              {item.label}
              <Badge n={item.badge} />
            </button>
          ))}
        </nav>
        <div className="ml-auto flex items-center gap-3">
          <button
            onClick={() => navigate('/create')}
            className="inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground shadow-md btn-bounce hover:brightness-110"
            data-testid="desktop-create-btn"
          >
            <Plus className="h-4 w-4" /> Publier
          </button>
          <button onClick={() => navigate('/profile')} className="rounded-full ring-2 ring-transparent hover:ring-primary/40 transition" data-testid="desktop-nav-avatar">
            <Avatar className="h-9 w-9">
              <AvatarImage src={user?.picture} />
              <AvatarFallback className="bg-secondary text-secondary-foreground font-bold">{user?.name?.charAt(0)}</AvatarFallback>
            </Avatar>
          </button>
        </div>
      </header>

      {/* Mobile bottom bar */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 glass border-t mobile-nav z-50" data-testid="bottom-nav">
        <div className="flex items-center justify-around px-2 pt-1.5 pb-2">
          {navItems.map((item) => {
            const active = isActive(item.path);
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`nav-item relative flex flex-col items-center gap-0.5 min-w-[56px] py-1.5 rounded-xl text-[11px] font-medium ${active ? 'active' : 'text-muted-foreground'}`}
                data-testid={testId(item.label)}
              >
                <span className={`relative rounded-xl px-3 py-1 transition-colors ${active ? 'bg-primary/10' : ''}`}>
                  <item.icon className={`h-5 w-5 ${active ? 'stroke-[2.4]' : ''}`} />
                  <Badge n={item.badge} />
                </span>
                {item.label}
              </button>
            );
          })}
        </div>
      </nav>
    </>
  );
};

export default BottomNav;
